import { useEffect } from 'react';

import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';

import useTargetAthleteProfile from './useTargetAthleteProfile';
import { useCheckout } from '../../CheckoutContext';

function usePrepopulateAthleteInfoEffect(): void {
  const { session } = useAuth();
  const { form, setAthleteInfoWasPrepopulated, athleteInfoWasPrepopulated } = useCheckout();

  const whoIsThisFor = form.values.whoIsThisFor;
  const { athleteProfile, isLoading: athleteLoading, athleteProfileId } = useTargetAthleteProfile();

  useEffect(() => {
    if (!!athleteProfileId && athleteLoading) {
      return;
    }

    if (athleteInfoWasPrepopulated) {
      return;
    }

    const firstName =
      whoIsThisFor === 'myself' && !athleteProfile ? session?.user.firstName : athleteProfile?.firstName;
    const lastName =
      whoIsThisFor === 'myself' && !athleteProfile ? session?.user.lastName : athleteProfile?.lastName;
    // The checkout mounts before the buyer signs in (the account step is inside the
    // accordion), so the form's initial email is empty for anyone who signed in there.
    // Fill it here from the target athlete, else the session — except for a new linked
    // profile, whose email the buyer types themselves.
    const email =
      athleteProfile?.email ??
      (whoIsThisFor === 'newLinkedAccount'
        ? form.values.athleteInformation.email
        : session?.user.email ?? '');

    form.setValues({
      ...form.values,
      athleteInformation: {
        ...form.values.athleteInformation,
        email,
        firstName: firstName ?? '',
        lastName: lastName ?? '',
        phone: athleteProfile?.phone ?? '',
        dob: athleteProfile?.dob ? dayjs(athleteProfile.dob).toDate() : null,
        gender: athleteProfile?.gender,
        address: athleteProfile?.address
          ? {
              addressLines: athleteProfile.address.addressLines ?? null,
              locality: athleteProfile.address.locality ?? null,
              region: athleteProfile.address.region ?? null,
              zipCode: athleteProfile.address.zipCode ?? null,
              country: athleteProfile.address.country ?? null,
            }
          : null,
        emergencyName: athleteProfile?.emergencyName,
        emergencyPhone: athleteProfile?.emergencyPhone,
        medicalNotes: athleteProfile?.medicalNotes,
      },
    });

    setAthleteInfoWasPrepopulated(true);
  }, [
    whoIsThisFor,
    athleteProfileId,
    session,
    athleteLoading,
    athleteProfile,
    form,
    athleteInfoWasPrepopulated,
    setAthleteInfoWasPrepopulated,
  ]);
}

export default usePrepopulateAthleteInfoEffect;
